import React, { useRef, useState } from 'react';
import { Button, Card, Modal, ModalHeader, ModalBody } from 'flowbite-react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiEdit, FiPlay, FiTrash2, FiUpload } from 'react-icons/fi';
import { useSnackbar } from 'notistack';
import * as sched from 'sched';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorAlert from '../../components/common/ErrorAlert';
import { useAuth } from '../../hooks/useAuth';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useAsync } from 'react-async-hook';
import WorkflowTemplateForm, { WorkflowTemplateFormData } from '../../components/forms/WorkflowTemplateForm';

const WorkflowsPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { client, token } = useAuth();
  const { workspaceId } = useWorkspace();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const importFileRef = useRef<HTMLInputElement>(null);
  const [importError, setImportError] = useState<{ message: string; templateId?: string } | null>(null);

  const workflowTemplatesResponse = useAsync(async () => {
    if (!client || !token || !workspaceId) return;
    return await sched.WrappedApiClient.list_workspace_workflow_templates(client, token, workspaceId);
  }, [token, workspaceId]);

  const handleCreateTemplate = () => {
    setShowCreateModal(true);
  };

  const handleCreateSubmit = async (data: WorkflowTemplateFormData) => {
    if (!client || !token || !workspaceId) return;

    try {
      setIsCreating(true);

      const request: sched.CreateWorkflowTemplateRequest = {
        title: data.title,
        description: data.description || '',
      };

      const createdTemplate = await sched.WrappedApiClient.create_workspace_workflow_template(
        client,
        token,
        workspaceId,
        request
      );

      // Emit a SetTitle event so the title is part of the versioned state
      await sched.WrappedApiClient.create_workflow_template_event(
        client, token, createdTemplate.id,
        { data: { type: 'SetTitle', title: data.title } } as any
      );

      enqueueSnackbar('Template created successfully', { variant: 'success' });
      setShowCreateModal(false);

      // Navigate to the template editor
      navigate(`/workspaces/${workspaceId}/workflows/templates/${createdTemplate.id}/edit`);
    } catch (err) {
      console.error('Failed to create template:', err);
      // TODO does this show a meaningful error
      enqueueSnackbar(`Failed to create template: ${err}`, { variant: 'error' });
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateCancel = () => {
    setShowCreateModal(false);
  };

  const handleEditTemplate = (templateId: string) => {
    navigate(`/workspaces/${workspaceId}/workflows/templates/${templateId}/edit`);
  };

  const handleStartWorkflow = async (template: sched.WorkflowTemplate, _templateVersion: number) => {
    if (!client || !token || !workspaceId) return;

    try {
      // Workflow instances are now created via data-graph commands
      // Navigate to workflow instances page to create a new one
      navigate(`/workspaces/${workspaceId}/workflow-instances?template=${template.id}`);
    } catch (err) {
      console.error('Failed to start workflow:', err);
      enqueueSnackbar('Failed to start workflow', { variant: 'error' });
    }
  };

  const handleDeleteTemplate = async (template: sched.WorkflowTemplate) => {
    if (!client || !token) return;

    if (!window.confirm(`Are you sure you want to delete "${template.title}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await sched.WrappedApiClient.delete_workflow_template(client, token, template.id);
      enqueueSnackbar('Template deleted successfully', { variant: 'success' });
      workflowTemplatesResponse.execute(); // Reload the list
    } catch (err) {
      console.error('Failed to delete template:', err);
      enqueueSnackbar('Failed to delete template', { variant: 'error' });
    }
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !client || !token || !workspaceId) return;
    e.target.value = '';

    let parsed: sched.WorkflowTemplateState;
    try {
      const text = await file.text();
      parsed = JSON.parse(text) as sched.WorkflowTemplateState;
      if (!parsed.steps || !Array.isArray(parsed.steps)) {
        enqueueSnackbar('Invalid workflow template file', { variant: 'error' });
        return;
      }
    } catch {
      enqueueSnackbar('Failed to parse JSON file', { variant: 'error' });
      return;
    }

    // Step 1: Create the template
    let createdTemplate: sched.WorkflowTemplate;
    try {
      const request: sched.CreateWorkflowTemplateRequest = {
        title: parsed.title || file.name.replace(/\.json$/, ''),
        description: '',
      };
      createdTemplate = await sched.WrappedApiClient.create_workspace_workflow_template(
        client, token, workspaceId, request
      );
    } catch (err) {
      enqueueSnackbar(`Failed to create template: ${err}`, { variant: 'error' });
      return;
    }

    // Step 2: Batch-create all steps and links as events
    try {
      const empty: sched.WorkflowTemplateState = {
        title: '', permissions: [], steps: [], links: [],
      };
      const events = sched.diff_workflow_template_states(empty, parsed);
      if (events.length > 0) {
        const request: sched.BatchCreateWorkflowTemplateEventsRequest = { events };
        await sched.WrappedApiClient.batch_create_workflow_template_events(
          client, token, createdTemplate.id, request
        );
      }
      enqueueSnackbar(`Imported "${parsed.title || file.name}" with ${parsed.steps.length} steps`, { variant: 'success' });
      navigate(`/workspaces/${workspaceId}/workflows/templates/${createdTemplate.id}/edit`);
    } catch (err) {
      console.error('Import batch events error:', err);
      setImportError({
        message: `Template was created but events failed to import: ${err}`,
        templateId: createdTemplate.id,
      });
    }
  };

  const handleDeleteFailedImport = async (templateId: string) => {
    if (!client || !token) return;
    try {
      await sched.WrappedApiClient.delete_workflow_template(client, token, templateId);
      enqueueSnackbar('Deleted partially-imported template', { variant: 'success' });
      setImportError(null);
      workflowTemplatesResponse.execute();
    } catch (err) {
      enqueueSnackbar(`Failed to delete template: ${err}`, { variant: 'error' });
    }
  };

  if (workflowTemplatesResponse.loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner message="Loading workflow templates..." />
      </div>
    );
  }

  if (workflowTemplatesResponse.error) {
    return <ErrorAlert message={workflowTemplatesResponse.error.message} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-cm-text-primary">
          Workflows
        </h1>
        <div className="flex items-center gap-2">
          <Button color="gray" onClick={() => importFileRef.current?.click()} className="flex items-center gap-2">
            <FiUpload className="w-4 h-4" />
            Import
          </Button>
          <Button onClick={handleCreateTemplate} className="flex items-center gap-2">
            <FiPlus className="w-4 h-4" />
            Create Template
          </Button>
        </div>
      </div>

      {/* Hidden file input for import */}
      <input ref={importFileRef} type="file" accept=".json" className="hidden" onChange={handleImportFile} />

      {/* Import failure banner */}
      {importError && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-800">{importError.message}</p>
          {importError.templateId && (
            <Button size="sm" color="failure" className="mt-2" onClick={() => handleDeleteFailedImport(importError.templateId!)}>
              <FiTrash2 className="w-3 h-3 mr-1" /> Delete failed template
            </Button>
          )}
        </div>
      )}

      {workflowTemplatesResponse.result?.templates.length === 0 ? (
        <Card>
          <div className="text-center py-8">
            <p className="text-cm-text-muted mb-4">
              No workflow templates found. Create your first template to get started.
            </p>
            <Button onClick={handleCreateTemplate} className="flex items-center gap-2 mx-auto">
              <FiPlus className="w-4 h-4" />
              Create Template
            </Button>
          </div>
        </Card>
      ) : (
        <Card>
          <div className="p-4">
            <h2 className="text-lg font-semibold text-cm-text-primary mb-4">
              Workflow Templates
            </h2>
            <div className="space-y-4">
              {workflowTemplatesResponse?.result?.templates.map((template) => (
                <div key={template.id} className="border border-cm-border-subtle rounded-lg p-4 bg-cm-bg-elevated">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium text-cm-text-primary">
                        {template.title}
                      </h3>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        color="gray"
                        onClick={() => handleEditTemplate(template.id)}
                        className="flex items-center gap-1"
                      >
                        <FiEdit className="w-3 h-3" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleStartWorkflow(template, 0)}
                        className="flex items-center gap-1"
                      >
                        <FiPlay className="w-3 h-3" />
                        Start
                      </Button>
                      <Button
                        size="sm"
                        color="failure"
                        onClick={() => handleDeleteTemplate(template)}
                        className="flex items-center gap-1"
                      >
                        <FiTrash2 className="w-3 h-3" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Create Template Modal */}
      <Modal show={showCreateModal} onClose={handleCreateCancel}>
        <ModalHeader>Create New Workflow Template</ModalHeader>
        <ModalBody>
          <WorkflowTemplateForm
            onSubmit={handleCreateSubmit}
            onCancel={handleCreateCancel}
            isSubmitting={isCreating}
          />
        </ModalBody>
      </Modal>
    </div>
  );
};

export default WorkflowsPage;
